'use client';

import type { CSSProperties } from 'react';

import {
	Captions,
	CaptionsOff,
	Maximize2,
	Minimize2,
	Pause,
	PictureInPicture2,
	Play,
	Volume1,
	Volume2,
	VolumeX,
} from 'lucide-react';

import { LogoFace } from './logo-face';
import { Button } from './button';
import { cn } from '../utils/cn';

import {
	PLAYBACK_RATES,
	formatTimestamp,
	useVideoPlayer,
} from './use-video-player';

interface VideoPlayerProps {
	/** Video file (mp4/webm). */
	src: string;
	/** Poster frame. Without one the fallback mark shows until the first frame. */
	poster?: string;
	/** WebVTT captions. Anything else must be converted first - see the note below. */
	captionsSrc?: string;
	/**
	 * Intrinsic pixel size - drives the aspect ratio, e.g. 2200 x 1160, AND the
	 * `--width` / `--height` custom properties the `viewport` variant clamps to.
	 * For that variant pass the size you want the film to REST at, not the
	 * source resolution (1200 x 675, not 1920 x 1080).
	 */
	width: number;
	height: number;
	/** Accessible name for the player region. */
	title?: string;
	className?: string;
	/** `inline` = a block in the page flow; `viewport` = a lightbox-sized film. */
	variant?: 'inline' | 'viewport';
	/**
	 * Which `min-aspect-ratio` rule the `viewport` variant switches on - or
	 * `native`: the frame takes the file's own ratio once metadata loads (the
	 * width/height props are only the pre-metadata placeholder) and the picture
	 * is letterboxed, never cropped. For arbitrary uploads (attachment lightbox).
	 */
	aspect?: '16:9' | '4:3' | 'native';
	/**
	 * `native` only: a CSS length the frame may not exceed vertically. The
	 * player derives the matching max-width itself, so a tall file shrinks to
	 * fit instead of overflowing.
	 */
	maxHeight?: string;
	/** Start playing on mount. Only honest after a user gesture (a lightbox open). */
	autoPlay?: boolean;
	/**
	 * Start muted. The mute button, `m` and the volume slider still work from
	 * there, and the volume readout reflects it. Autoplaying players must set
	 * this: browsers refuse an audible autoplay.
	 */
	muted?: boolean;
	/**
	 * Loop the film. Note that a looping `<video>` never fires `ended`, so the
	 * player's status stays `playing` for the whole session.
	 */
	loop?: boolean;
	/** Start with captions on (the lightbox does; inline players default off). */
	defaultCaptionsOn?: boolean;
}

/** Custom properties are plain numbers here (the stylesheet multiplies by 1%). */
const cssVars = (vars: Record<string, string | number>) => vars as CSSProperties;

/**
 * VideoPlayer - the marketing surfaces' inline film player, chrome and
 * all: play, volume with a vertical popover, a scrubber with buffered range and
 * a hover preview, playback rate, captions, PiP and fullscreen.
 *
 * The look is a deliberate 1:1 port of Linear's own player (see
 * `src/styles/video-player.css` for the ported rules and the token
 * substitutions). Every visual value lives in that stylesheet, not in
 * `className` - MARKETING_TYPE_SCALE.md rules out ad hoc arbitrary values on
 * marketing pages, and this chrome is mostly container queries, `:has()` state
 * and custom-property-driven ranges that Tailwind cannot express readably.
 *
 * Captions are WebVTT ONLY. The browser parses the `<track>`; we keep it in
 * `hidden` mode so it still fires `cuechange` while painting nothing, and render
 * the active cue ourselves so the placard matches the rest of the chrome. SRT is
 * not supported - `HeroFilmLightbox` passes the VTT twin of the film's SRT.
 *
 * Two variants: `inline` (a bordered block in the page flow) and `viewport` (the
 * lightbox film - `--width`/`--height` from the props clamp it, and on displays
 * wider than `aspect` the film is sized by HEIGHT so it never runs off screen).
 *
 * Fullscreen is requested on the ROOT element, not the video, so the custom
 * chrome comes along; iOS Safari falls back to the video's native presentation.
 */
export const VideoPlayer = ({
	src,
	poster,
	captionsSrc,
	width,
	height,
	title,
	className,
	variant = 'inline',
	aspect = '16:9',
	maxHeight,
	autoPlay = false,
	muted = false,
	loop = false,
	defaultCaptionsOn = false,
}: VideoPlayerProps) => {
	const { refs, state, actions } = useVideoPlayer({
		autoPlay,
		captionsSrc,
		defaultCaptionsOn,
		muted,
	});
	const { rootRef, videoRef, trackRef } = refs;

	// `native` follows the file's own ratio once known; everything else (and the
	// pre-metadata frame) uses the props.
	const frame =
		aspect === 'native' && state.intrinsic ? state.intrinsic : { width, height };
	const ratio = frame.width / frame.height;
	const containerStyle: CSSProperties = {
		aspectRatio: `${frame.width} / ${frame.height}`,
		...(aspect === 'native' && maxHeight
			? { maxWidth: `min(100%, calc(${maxHeight} * ${ratio}))`, marginInline: 'auto' }
			: {}),
	};

	const remaining = Math.max(0, state.duration - state.currentTime);
	const volumePercent = state.muted ? 0 : Math.round(state.volume * 100);
	const playing = state.status === 'playing';

	const VolumeIcon =
		state.muted || state.volume === 0 ? VolumeX : state.volume < 0.5 ? Volume1 : Volume2;
	const FullscreenIcon = state.fullscreen ? Minimize2 : Maximize2;

	return (
		<div
			ref={rootRef}
			className={cn('mkv-root dark frosted-ghosts', className)}
			style={cssVars({ '--width': `${width}px`, '--height': `${height}px` })}
			data-variant={variant}
			data-aspect={aspect}
			data-theme="dark"
			data-idle={state.idle ? 'true' : 'false'}
			data-status={state.status}
			role="region"
			aria-label={title ?? 'Media player'}
			tabIndex={0}
			onKeyDown={actions.handleKeyDown}
			onPointerMove={actions.markActive}
		>
			<div className="mkv-video-container" style={containerStyle}>
				{/* Sits behind the video, so it only shows while the frame is blank. */}
				<div className="mkv-fallback-poster" aria-hidden="true">
					<LogoFace variant="idle" className="size-10 text-white opacity-10" />
				</div>

				<video
					ref={videoRef}
					className="mkv-video"
					src={src}
					poster={poster}
					width={width}
					height={height}
					preload="metadata"
					playsInline
					autoPlay={autoPlay}
					muted={state.muted}
					loop={loop}
					onClick={actions.togglePlay}
					onPlay={() => actions.setStatus('playing')}
					onPause={() => actions.setStatus('paused')}
					onEnded={() => actions.setStatus('ended')}
					onTimeUpdate={actions.handleTimeUpdate}
					onLoadedMetadata={actions.handleLoadedMetadata}
					onProgress={actions.syncBuffered}
				>
					{captionsSrc && (
						<track ref={trackRef} kind="captions" src={captionsSrc} srcLang="en" label="English" />
					)}
				</video>

				<div className="mkv-container">
					<div className="mkv-chrome mkv-overlay" />

					{state.captionsOn && state.captionText && (
						<div className="mkv-captions" aria-live="polite">
							{state.captionText}
						</div>
					)}

					<div className="mkv-chrome mkv-controls">
						<Button
							variant="ghost"
							size="sm"
							iconOnly
							type="button"
							onClick={actions.togglePlay}
							aria-label={`${playing ? 'Pause' : 'Play'} (keyboard shortcut k)`}
							aria-keyshortcuts="k"
						>
							{playing ? <Pause /> : <Play />}
						</Button>

						<div className="mkv-sound">
							<Button
								variant="ghost"
								size="sm"
								iconOnly
								type="button"
								className="mkv-mute"
								data-sound
								onClick={actions.toggleMute}
								aria-label={`${state.muted ? 'Unmute' : 'Mute'} (keyboard shortcut m)`}
								aria-keyshortcuts="m"
							>
								<VolumeIcon />
							</Button>

							{/* Popover above the mute button - visibility, not display, so it
							    keeps its geometry and the pointer bridge in the CSS works. */}
							<div className="mkv-volume-positioner">
								<div className="mkv-volume-tint" />
								<div
									className="mkv-volume-control"
									style={cssVars({
										'--media-player-range-end': volumePercent,
										'--media-player-thumb-start': volumePercent,
									})}
									data-state={state.volumeInteracting ? 'interacting' : undefined}
									role="slider"
									aria-orientation="vertical"
									aria-label="Volume slider"
									aria-valuemin={0}
									aria-valuemax={100}
									aria-valuenow={volumePercent}
									aria-valuetext={`${volumePercent}%`}
									tabIndex={0}
									onPointerDown={actions.handleVolumePointerDown}
									onPointerMove={actions.handleVolumePointerMove}
									onPointerUp={actions.handleVolumePointerUp}
									onPointerCancel={actions.handleVolumePointerUp}
								>
									<div className="mkv-range" data-type="volume" />
									<div className="mkv-thumb mkv-volume-thumb" />
								</div>
							</div>
						</div>

						<div className="mkv-controls-group">
							<time
								className="mkv-time"
								role="timer"
								dateTime={`PT${Math.floor(state.currentTime)}S`}
							>
								<span className="sr-only">Elapsed </span>
								{formatTimestamp(state.currentTime)}
							</time>

							<div
								className="mkv-track mkv-progress"
								role="slider"
								aria-label="Seek slider"
								aria-valuemin={0}
								aria-valuemax={Math.round(state.duration)}
								aria-valuenow={Math.round(state.currentTime)}
								aria-valuetext={state.seekValueText}
								tabIndex={0}
								onPointerDown={actions.handleProgressPointerDown}
								onPointerMove={actions.handleProgressPointerMove}
								onPointerUp={actions.handleProgressPointerUp}
								onPointerCancel={actions.handleProgressPointerUp}
								onPointerLeave={actions.handleProgressPointerLeave}
							>
								<div className="mkv-track-mask">
									<div
										className="mkv-range"
										data-type="buffered"
										style={cssVars({
											'--media-player-range-start': state.buffered.start,
											'--media-player-range-end': state.buffered.end,
										})}
									/>
									<div
										className="mkv-range"
										data-type="played"
										style={cssVars({ '--media-player-range-end': state.playedPercent })}
									/>
								</div>

								<div
									className="mkv-thumb mkv-preview-line-thumb"
									style={cssVars({ '--media-player-thumb-start': state.previewPercent })}
								/>
								<div
									className="mkv-thumb mkv-timestamp-thumb mkv-time"
									style={cssVars({ '--media-player-thumb-start': state.previewPercent })}
									aria-label={`Seek to: ${formatTimestamp(state.previewSeconds)} / ${formatTimestamp(state.duration)}`}
								>
									{formatTimestamp(state.previewSeconds)}
								</div>
							</div>

							<time className="mkv-time" dateTime={`PT${Math.floor(remaining)}S`}>
								<span className="sr-only">Remaining </span>
								{'−'}
								{formatTimestamp(remaining)}
							</time>
						</div>

						<select
							className="mkv-glass-container mkv-playback-rate"
							value={state.playbackRate}
							onChange={(event) => actions.setPlaybackRate(Number(event.target.value))}
							aria-label="Change playback rate (keyboard shortcut > or <)"
							aria-keyshortcuts="Shift+. Shift+,"
						>
							{PLAYBACK_RATES.map((rate) => (
								<option key={rate} value={rate}>
									{rate}
									{'×'}
								</option>
							))}
						</select>

						{captionsSrc && (
							<Button
								variant="ghost"
								size="sm"
								iconOnly
								type="button"
								onClick={actions.toggleCaptions}
								aria-label={`${state.captionsOn ? 'Hide' : 'Show'} captions (keyboard shortcut c)`}
								aria-keyshortcuts="c"
								aria-pressed={state.captionsOn}
							>
								{state.captionsOn ? <Captions /> : <CaptionsOff />}
							</Button>
						)}

						{state.pipSupported && (
							<Button
								variant="ghost"
								size="sm"
								iconOnly
								type="button"
								className="mkv-pip"
								onClick={actions.togglePip}
								aria-label="Picture in picture (keyboard shortcut p)"
								aria-keyshortcuts="p"
							>
								<PictureInPicture2 />
							</Button>
						)}

						<Button
							variant="ghost"
							size="sm"
							iconOnly
							type="button"
							onClick={actions.toggleFullscreen}
							aria-label={`${state.fullscreen ? 'Exit fullscreen' : 'Fullscreen'} (keyboard shortcut f)`}
							aria-keyshortcuts="f"
						>
							<FullscreenIcon />
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};
